import React from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { Button, ButtonGroup, Badge } from 'react-bootstrap';
import { Edit, Trash2, Eye } from 'react-feather';
import { usePermission } from '../../../hooks/usePermission';
import { useManagerPermissions } from '../../../hooks/useManagerPermissions';
import { 
    setUpdateSparepartExpenseId, 
    setDeleteSparepartExpenseId 
} from '../../../actions/sparepart_expense';
import moment from 'moment';

const SparepartExpenseItem = ({ expense, number }) => {
    const { userCan } = usePermission();
    const { canManagerEditSparepartExpense, canManagerDeleteSparepartExpense } = useManagerPermissions();
    const dispatch = useDispatch();

    // Check if an expense has been updated
    const isExpenseUpdated = (expense) => {
        if (!expense.created_at || !expense.updated_at) return false;
        return moment(expense.created_at).format('YYYY-MM-DD HH:mm:ss') !== moment(expense.updated_at).format('YYYY-MM-DD HH:mm:ss');
    };

    // Determine if the item is deleted
    const isDeleted = !!expense.deleted_at;
    const expenseUpdated = isExpenseUpdated(expense);

    // Reconstruct expense object for permission checking
    const expenseObject = {
        id: expense.id,
        spare_part: expense.spare_part,
        quantity: expense.quantity,
        unit_price: expense.unit_price,
        warehouse: expense.warehouse,
        consumed_by_user: expense.consumed_by_user,
        expense_type: expense.expense_type,
        transport: expense.transport,
        machine: expense.machine,
        date: expense.date,
        firm_id: expense.firm_id,
        can_edit: expense.can_edit,
        created_at: expense.created_at,
        deleted_at: expense.deleted_at
    };

    // Check if user has any permissions for this expense
    const hasAnyPermission = canManagerEditSparepartExpense(expenseObject) || canManagerDeleteSparepartExpense(expenseObject);

    const handleEdit = () => {
        dispatch(setUpdateSparepartExpenseId(expense.id));
    };

    const handleDelete = () => {
        dispatch(setDeleteSparepartExpenseId(expense.id));
    };


    return (
        <tr className={isDeleted ? 'table-danger deleted-item' : ''}>
            <td className="fw-bold">{number}</td>
            <td>
                <div>
                    <div className="fw-semibold">
                        {expense.spare_part?.name}
                        {isDeleted && (
                            <Badge bg="danger" className="ms-2">O'chirilgan</Badge>
                        )}
                        {expenseUpdated && (
                            <Badge bg="warning" className="ms-2">O'zgartirilgan</Badge>
                        )}
                    </div>
                    <small className="text-muted">
                        {expense.spare_part?.unit} | 
                        Mavjud: {expense.spare_part?.current_quantity}
                    </small>
                </div>
            </td>
            <td>
                <span className="fw-semibold text-danger">-{parseFloat(expense.quantity).toFixed(2)}</span>
            </td>
            <td>
                <div>
                    <span className="fw-semibold text-success">
                        {expense.unit_price ? 
                            `${expense.unit_price} so'm` : 
                            '-'
                        }
                    </span>
                </div>
            </td>
            <td>
                <div>
                    <span className="fw-semibold text-primary">
                        {expense.total_amount ? 
                            `${expense.total_amount} so'm` : 
                            '-'
                        }
                    </span>
                </div>
            </td>
            <td>
                <div>
                    <div className="fw-semibold">{expense.warehouse?.firm?.name || "---"}</div>
                    <small className="text-muted">{expense.warehouse?.name || "---"}</small>
                </div>
            </td>
            <td>
                <div>
                    <div className="fw-semibold">{expense.consumed_by_user?.name}</div>
                    <small className="text-muted">Sarflagan</small>
                </div>
            </td>
            <td>
                <div>
                    {expense.expense_type === 'transport' && expense.transport && (
                        <div>
                            <div className="fw-semibold">{expense.transport?.name}</div>
                            <small className="text-muted">Transport</small>
                        </div>
                    )}
                    {expense.expense_type === 'machine' && expense.machine && (
                        <div>
                            <div className="fw-semibold">{expense.machine?.name}</div>
                            <small className="text-muted">Uskuna</small>
                        </div>
                    )}
                    {!expense.transport && !expense.machine && (
                        <span className="text-muted">-</span>
                    )}
                </div>
            </td>
            <td>
                <div>
                    <div>{expense.date}</div>
                    <small className="text-muted">{expense.created_at}</small>
                </div>
            </td>
            <td>
                <div>
                    {expense.notes ? (
                        <span className="text-wrap">{expense.notes}</span>
                    ) : (
                        <span className="text-muted">-</span>
                    )}
                </div>
            </td>
            <td>
                {!isDeleted ? (
                    <>
                        {hasAnyPermission ? (
                            <ButtonGroup size="sm">
                                {/* View Button */}
                                <Link to={`/sparepart-expenses/${expense.id}`}>
                                    <Button
                                        variant="outline-info"
                                        title="Ko'rish"
                                        size="sm"
                                    >
                                        <Eye size={16} />
                                    </Button>
                                </Link>

                                {/* Edit Button */}
                                {canManagerEditSparepartExpense(expenseObject) && (
                                    <Button
                                        variant="outline-warning"
                                        onClick={handleEdit}
                                        title="Tahrirlash"
                                    >
                                        <Edit size={16} />
                                    </Button>
                                )}

                                {/* Delete Button */}
                                {canManagerDeleteSparepartExpense(expenseObject) && (
                                    <Button
                                        variant="outline-danger"
                                        onClick={handleDelete}
                                        title="O'chirish"
                                    >
                                        <Trash2 size={16} />
                                    </Button>
                                )}
                            </ButtonGroup>
                        ) : (
                            // Show view button and expired badge when no actions are available
                            <div className="d-flex align-items-center gap-2">
                                <Link to={`/sparepart-expenses/${expense.id}`}>
                                    <Button
                                        variant="outline-info"
                                        size="sm"
                                        title="Ko'rish"
                                    >
                                        <Eye size={16} />
                                    </Button>
                                </Link>
                                {expense.can_edit === false ? (
                                    <Badge bg="warning" title="Tahrirlash muddati tugagan">
                                        Muddati o'tgan
                                    </Badge>
                                ) : (
                                    <Badge bg="secondary" title="Ruxsat yo'q">
                                        Ruxsat yo'q
                                    </Badge>
                                )}
                            </div>
                        )}
                    </>
                ) : (
                    // For deleted items, show nothing in actions column
                    <span className="text-muted">-</span>
                )}
            </td>
        </tr>
    );
};

SparepartExpenseItem.propTypes = {
    expense: PropTypes.shape({
        id: PropTypes.number.isRequired,
        spare_part: PropTypes.object,
        quantity: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        unit_price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        warehouse: PropTypes.object,
        consumed_by_user: PropTypes.object,
        purpose: PropTypes.string,
        description: PropTypes.string,
        date: PropTypes.string,
        reference_number: PropTypes.string,
        created_at: PropTypes.string,
    }).isRequired,
    number: PropTypes.number.isRequired,
};

export default SparepartExpenseItem; 